import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { clientsService } from '@/services/ClientsService'
import { companiesService } from '@/services/CompaniesService'
import { useAuthStore } from '@/stores/auth'
import { resolveClientsListScope } from '@/utils/clientScope'

const CLIENT_PAGE_SIZE = 100
// Tope de seguridad (10 000 clientes) para no quedar en un ciclo si el API no marca la última página.
const MAX_CLIENT_PAGES = 100

export const useClientsStore = defineStore('clients', () => {
  // State
  const clients = ref([])
  const isLoading = ref(false)
  const error = ref(null)
  const searchQuery = ref('')

  // Getters
  const activeClients = computed(() => 
    clients.value.filter(client => client.isActive)
  )

  const clientCount = computed(() => clients.value.length)

  const filteredClients = computed(() => {
    if (!searchQuery.value) return clients.value
    
    const query = searchQuery.value.toLowerCase()
    return clients.value.filter(client => 
      client.full_name.toLowerCase().includes(query) ||
      client.phone.includes(query) ||
      (client.email && client.email.toLowerCase().includes(query))
    )
  })

  const getClientById = computed(() => (id) => 
    clients.value.find(client => client.id === id)
  )

  const getClientByPhone = computed(() => (phone) => 
    clients.value.find(client => client.phone === phone)
  )

  const getClientByEmail = computed(() => (email) => 
    clients.value.find(client => client.email === email)
  )

  const frequentClients = computed(() => 
    clients.value
      .filter(client => client.totalAppointments >= 5)
      .sort((a, b) => b.totalAppointments - a.totalAppointments)
  )

  const newClients = computed(() => {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    
    return clients.value.filter(client => 
      new Date(client.created_at) >= thirtyDaysAgo
    )
  })

  const totalAppointments = computed(() => 
    clients.value.reduce((total, client) => total + (client.totalAppointments || 0), 0)
  )

  // Actions
  async function fetchClients() {
    isLoading.value = true
    error.value = null
    
    try {
      // Endpoint tenantizado: sin empresa activa no se pide (evita 400 y ruido en consola).
      if (!companiesService.hasActiveCompany()) {
        clients.value = []
        return
      }
      const activeCompany = companiesService.getActiveCompany()
      const auth = useAuthStore()
      const scope = resolveClientsListScope(auth.user?.role)
      // El API pagina (20 por defecto, máx. 100): se piden todas las páginas para no perder clientes.
      const all = []
      for (let page = 1; page <= MAX_CLIENT_PAGES; page++) {
        const response = await clientsService.getAll(activeCompany.id, { scope, limit: CLIENT_PAGE_SIZE, page })
        const raw = response.data ?? response
        const rows = Array.isArray(raw) ? raw : raw?.data ?? []
        all.push(...rows)
        const hasNext = response?.hasNextPage ?? raw?.hasNextPage ?? false
        if (!hasNext || rows.length === 0) break
      }
      clients.value = all
    } catch (err) {
      error.value = 'Error al cargar clientes'
      console.error('Error fetching clients:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function addClient(clientData) {
    isLoading.value = true
    error.value = null
    
    try {
      const activeCompany = companiesService.getActiveCompany()
      
      if (!activeCompany || !activeCompany.id) {
        throw new Error('No hay empresa activa')
      } 
      const response = await clientsService.create(activeCompany.id, clientData)
      const newClient = response.data || response
      
      // Agregar el cliente a la lista local
      clients.value.push(newClient)
      return newClient
    } catch (err) {
      error.value = 'Error al agregar cliente'
      console.error('Error adding client:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function mapClientPatch(clientData) {
    const patch = {}
    const has = (key) => Object.prototype.hasOwnProperty.call(clientData, key)

    if (has('clientName') || has('full_name')) {
      patch.full_name = String(clientData.full_name ?? clientData.clientName ?? '').trim()
    }
    if (has('phone')) patch.phone = clientData.phone
    if (has('email')) patch.email = clientData.email
    if (has('comments') || has('notes')) {
      patch.notes = clientData.notes ?? clientData.comments ?? ''
    }
    if (has('preferences')) patch.preferences = clientData.preferences

    return patch
  }

  function extractClientFromResponse(response) {
    const raw = response?.data ?? response
    if (!raw || typeof raw !== 'object') return null
    if (raw.data != null && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
      return raw.data
    }
    return raw
  }

  async function updateClient(id, clientData) {
    isLoading.value = true
    error.value = null

    const localPatch = mapClientPatch(clientData)

  const applyLocalPatch = (apiClient = null) => {
    const index = clients.value.findIndex(c => c.id === id)
    if (index === -1) return null

    const fromApi = apiClient ? normalizeClientRow(apiClient) : null
    const safeFromApi = fromApi
      ? Object.fromEntries(Object.entries(fromApi).filter(([, v]) => v !== undefined))
      : {}

    clients.value[index] = {
      ...clients.value[index],
      ...localPatch,
      ...safeFromApi,
      ...(localPatch.full_name != null ? { full_name: localPatch.full_name } : {}),
      ...(localPatch.notes != null ? { notes: localPatch.notes } : {})
    }
    return clients.value[index]
  }

    try {
      const activeCompany = companiesService.getActiveCompany()

      if (!activeCompany || !activeCompany.id) {
        return applyLocalPatch()
      }

      const response = await clientsService.update(id, clientData, activeCompany.id)
      const updatedClient = extractClientFromResponse(response)
      return applyLocalPatch(updatedClient)
    } catch (err) {
      error.value = 'Error al actualizar cliente'
      console.error('Error updating client:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function deleteClient(id) {
    isLoading.value = true
    error.value = null
    
    try {
      await clientsService.delete(id)
      
      // Eliminar de la lista local
      const index = clients.value.findIndex(client => client.id === id)
      if (index !== -1) {
        const deletedClient = clients.value.splice(index, 1)[0]
        return deletedClient
      }
    } catch (err) {
      error.value = 'Error al eliminar cliente'
      console.error('Error deleting client:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function toggleClientStatus(id) {
    const client = clients.value.find(c => c.id === id)
    
    if (client) {
      const isActive = client.isActive ?? client.is_active ?? true
      const response = isActive
        ? await clientsService.deactivate(id)
        : await clientsService.reactivate(id)
      const updatedClient = response?.data || response
      client.isActive = updatedClient?.isActive ?? updatedClient?.is_active ?? !isActive
      client.is_active = updatedClient?.is_active ?? updatedClient?.isActive ?? !isActive
      return client
    }
    
    throw new Error('Cliente no encontrado')
  }

  function incrementAppointmentCount(clientId) {
    const client = clients.value.find(c => c.id === clientId)
    
    if (client) {
      client.totalAppointments = (client.totalAppointments || 0) + 1
      client.lastAppointment = new Date().toISOString().split('T')[0]
      return client
    }
    
    throw new Error('Cliente no encontrado')
  }

  function updateClientPreferences(clientId, preferences) {
    const client = clients.value.find(c => c.id === clientId)
    
    if (client) {
      client.preferences = { ...client.preferences, ...preferences }
      return client
    }
    
    throw new Error('Cliente no encontrado')
  }

  function searchClients(query) {
    searchQuery.value = query
  }

  function clearSearch() {
    searchQuery.value = ''
  }

  /** Normaliza un cliente devuelto por la API (búsqueda o listado). */
  function normalizeClientRow(raw) {
    if (!raw || typeof raw !== 'object') return null
    return {
      id: raw.id,
      full_name: raw.full_name ?? raw.fullName ?? '',
      phone: raw.phone ?? '',
      email: raw.email ?? '',
      notes: raw.notes ?? raw.company_notes ?? '',
      company_notes: raw.company_notes ?? raw.notes ?? '',
      isActive: raw.is_active !== false && raw.isActive !== false
    }
  }

  /**
   * Búsqueda en servidor (tenant + usuario en headers). Mín. 2 caracteres.
   * @returns {Promise<Array<{ id, full_name, phone, email }>>}
   */
  async function searchClientsRemote(query) {
    const q = String(query || '').trim()
    if (q.length < 2) return []
    const activeCompany = companiesService.getActiveCompany()
    if (!activeCompany?.id) return []
    const auth = useAuthStore()
    const scope = resolveClientsListScope(auth.user?.role)
    // Por /clients?q= y no /clients/search: ese exige email o teléfono y devuelve UN cliente.
    const response = await clientsService.getAll(activeCompany.id, { scope, q, limit: 50 })
    const raw = response.data ?? response
    const list = Array.isArray(raw) ? raw : raw?.data
    if (!Array.isArray(list)) return []
    return list.map(normalizeClientRow).filter(Boolean)
  }

  /** Consulta un cliente por id en la empresa activa; null si no existe en ella (404/403). */
  async function fetchClientOfActiveCompany(clientId) {
    try {
      const response = await clientsService.getById(clientId)
      const raw = response?.data ?? response
      return normalizeClientRow(raw?.data ?? raw)
    } catch (err) {
      if (err?.status === 404 || err?.status === 403) return null
      throw err
    }
  }

  /**
   * Obtiene o crea el cliente para guardar la cita.
   * Si viene `clientId`, debe existir en la empresa activa; no se adopta un id externo.
   */
  async function resolveClientForAppointment(appointmentData) {
    const existingId = appointmentData.clientId
    if (existingId) {
      if (clients.value.length === 0 && !isLoading.value) {
        await fetchClients()
      }
      const cached = clients.value.find((c) => String(c.id) === String(existingId))
      if (cached) return cached
      // La lista local es solo la primera página; el buscador puede elegir clientes más allá de ella.
      const fetched = await fetchClientOfActiveCompany(existingId)
      if (!fetched) {
        throw new Error('El cliente seleccionado no pertenece a la empresa activa. Vuelve a seleccionarlo.')
      }
      clients.value.push(fetched)
      return fetched
    }
    const phone = (appointmentData.phone || '').trim()
    let client = clients.value.find((c) => c.phone === phone)
    if (!client) {
      client = await addClient({
        clientName: appointmentData.clientName,
        phone: appointmentData.phone,
        email: appointmentData.email || '',
        comments: appointmentData.notes || ''
      })
    }
    return client
  }

  // Validaciones
  function validateClient(clientData) {
    const errors = []
    const has = (key) => Object.prototype.hasOwnProperty.call(clientData, key)
    const isUpdate = clientData.id != null

    if (!isUpdate || has('clientName') || has('full_name')) {
      const name = clientData.clientName ?? clientData.full_name
      if (!name || String(name).trim().length < 2) {
        errors.push('El nombre debe tener al menos 2 caracteres')
      }
    }

    if (!isUpdate || has('phone')) {
      const phone = clientData.phone
      if (!phone || String(phone).trim().length < 10) {
        errors.push('El teléfono debe tener al menos 10 caracteres')
      }
    }

    if (has('email') && clientData.email && !isValidEmail(clientData.email)) {
      errors.push('El email debe tener un formato válido')
    }

    if (has('phone')) {
      const existingClientByPhone = clients.value.find(c =>
        c.phone === clientData.phone && c.id !== clientData.id
      )

      if (existingClientByPhone) {
        errors.push('Ya existe un cliente con ese número de teléfono')
      }
    }

    if (has('email') && clientData.email) {
      const existingClientByEmail = clients.value.find(c =>
        c.email === clientData.email && c.id !== clientData.id
      )

      if (existingClientByEmail) {
        errors.push('Ya existe un cliente con ese email')
      }
    }

    return errors
  }

  // Utilidades de validación
  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Estadísticas
  function getClientStats() {
    return {
      total: clientCount.value,
      active: activeClients.value.length,
      new: newClients.value.length,
      frequent: frequentClients.value.length,
      totalAppointments: totalAppointments.value,
      averageAppointments: clientCount.value > 0 
        ? Math.round(totalAppointments.value / clientCount.value) 
        : 0
    }
  }

  // Reset
  function $reset() {
    clients.value = []
    isLoading.value = false
    error.value = null
    searchQuery.value = ''
  }

  return {
    // State
    clients,
    isLoading,
    error,
    searchQuery,
    
    // Getters
    activeClients,
    clientCount,
    filteredClients,
    getClientById,
    getClientByPhone,
    getClientByEmail,
    frequentClients,
    newClients,
    totalAppointments,
    
    // Actions
    fetchClients,
    addClient,
    updateClient,
    deleteClient,
    toggleClientStatus,
    incrementAppointmentCount,
    updateClientPreferences,
    searchClients,
    searchClientsRemote,
    resolveClientForAppointment,
    clearSearch,
    validateClient,
    getClientStats,
    $reset
  }
})
